import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { MarketStatus } from '@sportspredict/shared';
import { useGetLeaderboardQuery } from '@/store/api';
import { MARKET_SECTIONS } from '@/constants';

interface UseLobbySettledWinnerProps {
	eventId: string | undefined;
	lobbyId: string | undefined;
	lobbyResponse: any;
	isCrowdLockMode: boolean;
	markets: any[];
	slateFiltersState: any;
}

export function useLobbySettledWinner({
	eventId,
	lobbyId,
	lobbyResponse,
	isCrowdLockMode,
	markets,
	slateFiltersState,
}: UseLobbySettledWinnerProps) {
	const currentLeaderboardFilters = slateFiltersState?.tabs?.[MARKET_SECTIONS.Leaderboard];

	const settledSlates = useMemo(() => {
		const slates = currentLeaderboardFilters?.all?.slates;
		if (!slates || !markets) return [];

		return slates.filter((slate: any) => {
			const matchIds = slate.matches?.map((m: any) => m.id);
			if (!matchIds?.length) return false;

			const slateMarkets = markets.filter((m: any) => m.match_id && matchIds.includes(m.match_id));

			if (!slateMarkets.length) return false;

			return slateMarkets.every((m: any) => m.status === MarketStatus.Settled);
		});
	}, [currentLeaderboardFilters?.all?.slates, markets]);

	const lastSettledSlate = useMemo(() => {
		if (!settledSlates.length) return null;

		return [...settledSlates].sort((a: any, b: any) => {
			const aDate = new Date(a.closing_date || 0).getTime();
			const bDate = new Date(b.closing_date || 0).getTime();
			return aDate - bDate;
		})[settledSlates.length - 1];
	}, [settledSlates]);

	const settledMatchIds = lastSettledSlate?.matches?.map((m: any) => m.id) ?? [];

	const { data: settledLeaderboard } = useGetLeaderboardQuery(
		eventId && lobbyId && settledMatchIds?.length > 0 && lobbyResponse && !isCrowdLockMode
			? { eventId, lobbyId, matchIds: settledMatchIds, isOverall: false }
			: skipToken,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const winnerName = useMemo(() => {
		if (!settledLeaderboard?.length) {
			return '';
		}
		const first = settledLeaderboard[0];
		return first.full_name || 'Our first player';
	}, [settledLeaderboard]);

	return { winnerName };
}
